/**
 * Validadores de negócio compartilhados (espelham backend Python).
 *
 * Backend de referência: PMOC_123/apps/clientes/validators.py.
 * Mantemos paridade do algoritmo de DV pra evitar divergência cliente/servidor.
 */

/**
 * Remove qualquer caractere não-dígito.
 */
export function extrairDigitos(valor: string): string {
  return valor.replace(/\D/g, '')
}

function calcDigito(digitos: string, pesos: number[]): number {
  const soma = pesos.reduce((acc, p, i) => acc + parseInt(digitos[i], 10) * p, 0)
  const d = 11 - (soma % 11)
  return d >= 10 ? 0 : d
}

/**
 * Valida um CNPJ com cálculo dos dígitos verificadores (mod 11).
 * Aceita entrada com ou sem máscara. Retorna `true` para vazio (deixa
 * a validação de obrigatório a cargo do schema).
 *
 * Reprova:
 *   - tamanho diferente de 14 dígitos
 *   - todos os dígitos repetidos (00000000000000, 11111111111111, ...)
 *   - DV1 ou DV2 incorretos
 */
export function isCnpjValido(valor: string): boolean {
  if (!valor) return true
  const digitos = extrairDigitos(valor)
  if (digitos.length !== 14) return false
  if (new Set(digitos).size === 1) return false

  const pesos1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
  const d1 = calcDigito(digitos, pesos1)
  const pesos2 = [6, ...pesos1]
  const d2 = calcDigito(digitos, pesos2)

  return parseInt(digitos[12], 10) === d1 && parseInt(digitos[13], 10) === d2
}

/**
 * Mensagem de erro detalhada para Zod refine. Retorna string vazia em sucesso.
 */
export function mensagemErroCnpj(valor: string): string {
  if (!valor) return ''
  const digitos = extrairDigitos(valor)
  if (digitos.length !== 14) return 'CNPJ deve ter 14 dígitos'
  if (new Set(digitos).size === 1) return 'CNPJ inválido'
  return isCnpjValido(valor) ? '' : 'CNPJ inválido — confira o número digitado'
}

/**
 * Aplica máscara progressiva XX.XXX.XXX/XXXX-XX em CNPJ. Aceita qualquer
 * entrada (com ou sem máscara, parcial ou completa) e devolve o que couber.
 * Trunca em 14 dígitos.
 */
export function formatarCnpj(valor: string): string {
  const d = extrairDigitos(valor).slice(0, 14)
  if (d.length <= 2) return d
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`
  if (d.length <= 12)
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`
}

/**
 * Aplica máscara progressiva XXXXX-XXX em CEP. Trunca em 8 dígitos.
 */
export function formatarCep(valor: string): string {
  const d = extrairDigitos(valor).slice(0, 8)
  if (d.length <= 5) return d
  return `${d.slice(0, 5)}-${d.slice(5)}`
}
